import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  ConfirmDialog,
  DangerButton,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import {
  CHECK_FIELD_TYPES,
  CHECK_FIELD_TYPE_LABELS,
  SAMPLE_CHECK_VALUES,
  createCheckField,
} from "../../../utility/checkFieldTypes";
import { PRIZE_LINE_COLUMN_OPTIONS } from "../../../utility/checkPrizeLines";
import {
  defaultCheckFields,
  nextFieldInBand,
} from "../../../utility/checkDefaultLayouts";
import {
  VOUCHER_BANDS,
  bandForYInches,
  clampFieldToBand,
  pageLayoutBandHeight,
} from "../../../utility/checkGeometry";
import {
  deleteCheckTemplate,
  getCheckTemplate,
  saveCheckTemplate,
} from "../../../utility/checkTemplates";
import { downloadCalibrationPdf } from "../../../utility/generateChecks";
import CheckPageCanvas from "./CheckPageCanvas";
import ScanCornerEditor from "./ScanCornerEditor";
import {
  DEFAULT_SCAN_CORNERS,
  FULL_PHOTO_CORNERS,
  canvasToObjectUrl,
  loadOrientedImage,
  warpImageToLetter,
} from "../../../utility/checkScanWarp";

const inputClass =
  "w-full p-2 text-sm border border-brand-charcoal/20 rounded-lg focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent";

const CheckTemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canManageTournaments } = usePermissions();
  const [template, setTemplate] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [scanUrl, setScanUrl] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [scanCorners, setScanCorners] = useState(DEFAULT_SCAN_CORNERS);
  const [adjustingScan, setAdjustingScan] = useState(false);
  const [applyingScan, setApplyingScan] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);
  const [addFieldType, setAddFieldType] = useState("payeeName");
  const [addBandId, setAddBandId] = useState("check");
  const [addToOtherStub, setAddToOtherStub] = useState(false);
  const [scanFileName, setScanFileName] = useState("");
  const scanUrlRef = useRef("");
  const sourceUrlRef = useRef("");
  const sourceImageRef = useRef(null);
  const scanFileInputRef = useRef(null);

  const clearScan = () => {
    if (scanUrlRef.current) URL.revokeObjectURL(scanUrlRef.current);
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    scanUrlRef.current = "";
    sourceUrlRef.current = "";
    sourceImageRef.current = null;
    setScanUrl("");
    setSourceUrl("");
    setScanFileName("");
    setScanCorners(DEFAULT_SCAN_CORNERS);
    setAdjustingScan(false);
    setApplyingScan(false);
    if (scanFileInputRef.current) scanFileInputRef.current.value = "";
  };

  useEffect(() => () => clearScan(), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const row = await getCheckTemplate(templateId);
        if (!cancelled) {
          if (!row) setError("That check template was not found.");
          else if (!row.fields?.length) {
            setTemplate({
              ...row,
              fields: defaultCheckFields(row.pageLayout),
            });
          } else setTemplate(row);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Could not load this check template.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const selectedField = useMemo(
    () => template?.fields?.find((field) => field.id === selectedId) || null,
    [template, selectedId]
  );

  const fieldsByBand = useMemo(() => {
    if (!template) return [];
    const bands =
      template.pageLayout === "threePerPage"
        ? [{ id: "check", label: "Check", topInches: 0, heightInches: 3.5 }]
        : VOUCHER_BANDS;
    return bands.map((band) => ({
      ...band,
      fields: (template.fields || []).filter(
        (field) =>
          bandForYInches(field.yInches, template.pageLayout).id === band.id
      ),
    }));
  }, [template]);

  const selectedBand = selectedField
    ? bandForYInches(selectedField.yInches, template?.pageLayout)
    : null;

  const updateTemplate = (patch) => {
    setTemplate((current) => (current ? { ...current, ...patch } : current));
  };

  const updateField = (fieldId, patch) => {
    setTemplate((current) => {
      if (!current) return current;
      const bandHeight = pageLayoutBandHeight(current.pageLayout);
      return {
        ...current,
        fields: current.fields.map((field) =>
          field.id === fieldId
            ? clampFieldToBand({ ...field, ...patch }, bandHeight)
            : field
        ),
      };
    });
  };

  const addField = (fieldType, bandId = addBandId, alsoOtherStub = false) => {
    setTemplate((current) => {
      if (!current) return current;
      const bandHeight = pageLayoutBandHeight(current.pageLayout);
      const first = clampFieldToBand(
        nextFieldInBand(fieldType, current.fields, current.pageLayout, bandId),
        bandHeight
      );
      const fields = [...current.fields, first];
      if (
        alsoOtherStub &&
        current.pageLayout === "voucher" &&
        bandId !== "check"
      ) {
        const otherId = bandId === "payeeCopy" ? "officeCopy" : "payeeCopy";
        fields.push(
          clampFieldToBand(
            nextFieldInBand(fieldType, fields, current.pageLayout, otherId),
            bandHeight
          )
        );
      }
      setSelectedId(first.id);
      return { ...current, fields };
    });
  };

  const applyDefaultLayout = () => {
    setTemplate((current) =>
      current
        ? { ...current, fields: defaultCheckFields(current.pageLayout) }
        : current
    );
    setSelectedId(null);
  };

  const copyToOtherStub = () => {
    if (!selectedField || template?.pageLayout !== "voucher") return;
    const band = bandForYInches(selectedField.yInches, "voucher");
    if (band.id === "check") return;
    const dest = band.id === "payeeCopy" ? VOUCHER_BANDS[2] : VOUCHER_BANDS[1];
    const height = Number(selectedField.heightInches) || 0.2;
    const offset = selectedField.yInches - band.topInches;
    const maxY = dest.topInches + dest.heightInches - height;
    const copy = clampFieldToBand(
      {
        ...createCheckField(selectedField.fieldType, template.fields),
        ...selectedField,
        id:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `field-${Date.now()}-copy`,
        yInches: Math.min(
          maxY,
          Math.max(dest.topInches, dest.topInches + offset)
        ),
      },
      pageLayoutBandHeight("voucher")
    );
    setTemplate((current) =>
      current ? { ...current, fields: [...current.fields, copy] } : current
    );
    setSelectedId(copy.id);
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setTemplate((current) =>
      current
        ? {
            ...current,
            fields: current.fields.filter((field) => field.id !== selectedId),
          }
        : current
    );
    setSelectedId(null);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (!selectedId || !template) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        removeSelected();
        return;
      }
      const nudge = event.shiftKey ? 0.1 : 0.05;
      const field = template.fields.find((item) => item.id === selectedId);
      if (!field) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        updateField(selectedId, { xInches: field.xInches - nudge });
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        updateField(selectedId, { xInches: field.xInches + nudge });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        updateField(selectedId, { yInches: field.yInches - nudge });
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        updateField(selectedId, { yInches: field.yInches + nudge });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, template]);

  const applyScan = async (nextCorners = scanCorners) => {
    const image = sourceImageRef.current;
    if (!image) return;
    setApplyingScan(true);
    setError("");
    try {
      const canvas = warpImageToLetter(image, nextCorners);
      const url = await canvasToObjectUrl(canvas);
      if (scanUrlRef.current) URL.revokeObjectURL(scanUrlRef.current);
      scanUrlRef.current = url;
      setScanUrl(url);
      setScanCorners(nextCorners);
      setAdjustingScan(false);
    } catch (err) {
      console.error(err);
      setError(
        err.message || "Could not line that photo up. Move the corners and try again."
      );
    } finally {
      setApplyingScan(false);
    }
  };

  const handleScan = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Use a photo of a blank check.");
      return;
    }
    clearScan();
    setError("");
    try {
      const loaded = await loadOrientedImage(file);
      sourceUrlRef.current = loaded.url;
      sourceImageRef.current = loaded.image;
      setSourceUrl(loaded.url);
      setScanFileName(file.name);
      setScanCorners(DEFAULT_SCAN_CORNERS);
      setAdjustingScan(true);
    } catch (err) {
      console.error(err);
      setError(err.message || "Use a JPG or PNG photo of a blank check.");
    }
  };

  const handleSave = async () => {
    if (!template || !user) return false;
    setSaving(true);
    setError("");
    try {
      const saved = await saveCheckTemplate({ template, userId: user.id });
      setTemplate(saved);
      setSettingsOpen(false);
      setSettingsSnapshot(null);
      return true;
    } catch (err) {
      console.error(err);
      setError("Could not save this template.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCalibration = async () => {
    setCalibrating(true);
    setError("");
    try {
      const saved = await handleSave();
      if (!saved) return;
      await downloadCalibrationPdf(templateId);
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not create the calibration PDF.");
    } finally {
      setCalibrating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCheckTemplate({ templateId, userId: user.id });
      navigate("/organization/check-templates");
    } catch (err) {
      console.error(err);
      setError("Could not delete this template.");
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div
        className="space-y-4 sm:space-y-6"
        role="status"
        aria-label="Loading check template"
      >
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={8} />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Link
          to="/organization/check-templates"
          className="inline-flex items-center gap-2 text-sm text-brand-charcoal hover:underline"
        >
          <FaArrowLeft className="h-3 w-3" />
          All check templates
        </Link>
        <InlineAlert
          type="error"
          title="Template not found"
          message={error || "That check template was not found."}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <Link
            to="/organization/check-templates"
            className="inline-flex items-center gap-2 text-sm text-brand-mist hover:text-brand-cream mb-2"
          >
            <FaArrowLeft className="h-3 w-3" />
            All check templates
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                {template.name}
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                {template.pageLayout === "threePerPage"
                  ? "Place fields on the top 3.5 inch band. The other two checks use the same layout."
                  : "Check on top, prize lines on both stubs. Upload a blank check as a guide. The photo is not saved."}
              </p>
            </div>
            {canManageTournaments ? (
              <PrimaryButton
                onClick={handleSave}
                loading={saving}
                className="shrink-0"
              >
                Save template
              </PrimaryButton>
            ) : null}
          </div>
        </div>
        <div className="px-6 py-4 space-y-4">
          {!settingsOpen ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <dl className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <div>
                  <dt className="text-xs text-brand-muted">Layout</dt>
                  <dd>
                    {template.pageLayout === "threePerPage"
                      ? "3 per page"
                      : "Voucher / 3-part"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-brand-muted">Print offset</dt>
                  <dd>
                    {template.offsetXInches || 0} in right,{" "}
                    {template.offsetYInches || 0} in down
                  </dd>
                </div>
              </dl>
              {canManageTournaments ? (
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                  <SecondaryButton
                    onClick={handleCalibration}
                    disabled={calibrating || saving}
                    className="!px-3 !py-2 text-sm !rounded-md !tracking-normal"
                  >
                    Print calibration PDF
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setSettingsSnapshot({
                        name: template.name,
                        pageLayout: template.pageLayout,
                        bandHeightInches: template.bandHeightInches,
                        offsetXInches: template.offsetXInches,
                        offsetYInches: template.offsetYInches,
                      });
                      setSettingsOpen(true);
                    }}
                    className="!px-3 !py-2 text-sm !rounded-md !tracking-normal"
                  >
                    Edit
                  </SecondaryButton>
                  <DangerButton
                    onClick={() => setConfirmDelete(true)}
                    disabled={saving}
                    icon={FaTrash}
                  >
                    Delete template
                  </DangerButton>
                </div>
              ) : null}
            </div>
          ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs text-brand-muted mb-1">
                Template name
              </span>
              <input
                className={inputClass}
                value={template.name}
                onChange={(event) =>
                  updateTemplate({ name: event.target.value })
                }
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-brand-muted mb-1">
                Page layout
              </span>
              <select
                className={inputClass}
                value={template.pageLayout}
                onChange={(event) => {
                  const pageLayout = event.target.value;
                  const bandHeightInches = pageLayoutBandHeight(pageLayout);
                  if (pageLayout === "threePerPage") setAddBandId("check");
                  updateTemplate({
                    pageLayout,
                    bandHeightInches,
                    fields:
                      pageLayout === "threePerPage"
                        ? (template.fields || []).map((field) =>
                            clampFieldToBand(field, bandHeightInches)
                          )
                        : template.fields,
                  });
                }}
              >
                <option value="voucher">Voucher / 3-part</option>
                <option value="threePerPage">3 per page</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-brand-muted mb-1">
                Horizontal offset (inches)
              </span>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={template.offsetXInches}
                onChange={(event) =>
                  updateTemplate({ offsetXInches: Number(event.target.value) })
                }
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-brand-muted mb-1">
                Vertical offset (inches)
              </span>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={template.offsetYInches}
                onChange={(event) =>
                  updateTemplate({ offsetYInches: Number(event.target.value) })
                }
              />
            </label>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
              <PrimaryButton onClick={handleSave} loading={saving}>
                Save
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  if (settingsSnapshot) updateTemplate(settingsSnapshot);
                  setSettingsSnapshot(null);
                  setSettingsOpen(false);
                }}
                className="!px-3 !py-2 text-sm !rounded-md !tracking-normal"
              >
                Cancel
              </SecondaryButton>
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                <SecondaryButton
                  onClick={handleCalibration}
                  disabled={calibrating || saving}
                  className="!px-3 !py-2 text-sm !rounded-md !tracking-normal"
                >
                  Print calibration PDF
                </SecondaryButton>
                <DangerButton
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  icon={FaTrash}
                >
                  Delete template
                </DangerButton>
              </div>
            </div>
          </div>
          )}
        </div>
      </header>

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <div className="card-board p-2 sm:p-3 space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 min-w-0">
              <input
                ref={scanFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleScan}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
              />
              <SecondaryButton
                className="!px-3 !py-1 text-xs shrink-0"
                onClick={() => scanFileInputRef.current?.click()}
                aria-label={
                  scanFileName
                    ? "Replace the blank check photo"
                    : "Upload a photo of a blank check"
                }
              >
                {scanFileName ? "Replace photo" : "Upload photo"}
              </SecondaryButton>
              {scanFileName ? (
                <span
                  className="text-xs text-brand-muted truncate max-w-[14rem]"
                  title={scanFileName}
                >
                  {scanFileName}
                </span>
              ) : null}
            </div>
            {sourceUrl ? (
              <>
                {scanUrl && !adjustingScan ? (
                  <SecondaryButton
                    onClick={() => setAdjustingScan(true)}
                    className="!px-3 !py-1 text-xs"
                  >
                    Adjust corners
                  </SecondaryButton>
                ) : null}
                <SecondaryButton
                  onClick={clearScan}
                  className="!px-3 !py-1 text-xs"
                >
                  Remove photo
                </SecondaryButton>
              </>
            ) : null}
            {canManageTournaments ? (
              <SecondaryButton
                className="!px-3 !py-1 text-xs"
                onClick={() => setConfirmReset(true)}
              >
                Use default layout
              </SecondaryButton>
            ) : null}
            <label className="ml-auto text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={showPreview}
                onChange={(event) => setShowPreview(event.target.checked)}
              />
              Sample values
            </label>
          </div>
          {adjustingScan && sourceUrl ? (
            <ScanCornerEditor
              imageUrl={sourceUrl}
              corners={scanCorners}
              applying={applyingScan}
              onChangeCorners={setScanCorners}
              onApply={() => applyScan()}
              onUseFullPhoto={() => applyScan(FULL_PHOTO_CORNERS)}
            />
          ) : (
            <CheckPageCanvas
              pageLayout={template.pageLayout}
              fields={template.fields || []}
              selectedId={selectedId}
              scanUrl={scanUrl}
              showPreview={showPreview}
              previewValues={SAMPLE_CHECK_VALUES}
              onSelect={setSelectedId}
              onChangeField={updateField}
            />
          )}
          <p className="text-xs text-brand-muted">
            Print at 100% / actual size. Turn off fit to page.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 lg:w-80 xl:w-[34rem] xl:sticky xl:top-4 xl:self-start">
        <div className="card-board p-4 space-y-4">
          <div className="space-y-2">
            <h2 className="heading-display text-base">Add field</h2>
            <label className="block text-sm">
              <span className="block text-xs text-brand-muted mb-1">
                Field type
              </span>
              <select
                className={inputClass}
                value={addFieldType}
                disabled={!canManageTournaments}
                onChange={(event) => setAddFieldType(event.target.value)}
              >
                {CHECK_FIELD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            {template.pageLayout === "voucher" ? (
              <label className="block text-sm">
                <span className="block text-xs text-brand-muted mb-1">
                  Place on
                </span>
                <select
                  className={inputClass}
                  value={addBandId}
                  disabled={!canManageTournaments}
                  onChange={(event) => setAddBandId(event.target.value)}
                >
                  {VOUCHER_BANDS.map((band) => (
                    <option key={band.id} value={band.id}>
                      {band.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {template.pageLayout === "voucher" && addBandId !== "check" ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={addToOtherStub}
                  disabled={!canManageTournaments}
                  onChange={(event) => setAddToOtherStub(event.target.checked)}
                />
                Also add to the other stub
              </label>
            ) : null}
            {canManageTournaments ? (
              <PrimaryButton
                onClick={() => addField(addFieldType, addBandId, addToOtherStub)}
              >
                Add field
              </PrimaryButton>
            ) : null}
          </div>

          <div className="space-y-3">
            <h2 className="heading-display text-base">Fields on this page</h2>
            {fieldsByBand.map((band) => (
              <div key={band.id}>
                <h3 className="text-xs uppercase tracking-wide text-brand-muted mb-1">
                  {band.label}
                </h3>
                {band.fields.length === 0 ? (
                  <p className="text-sm text-brand-muted">None</p>
                ) : (
                  <ul className="space-y-1">
                    {band.fields.map((field) => {
                      const selected = field.id === selectedId;
                      return (
                        <li key={field.id}>
                          <button
                            type="button"
                            className={`w-full text-left text-sm px-2 py-1 rounded ${
                              selected
                                ? "bg-brand-gold/30 text-brand-charcoal"
                                : "hover:bg-brand-gold/10"
                            }`}
                            onClick={() => setSelectedId(field.id)}
                          >
                            {CHECK_FIELD_TYPE_LABELS[field.fieldType] ||
                              field.fieldType}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card-board p-4 space-y-4">
          {selectedField ? (
            <>
              <div>
                <h2 className="heading-display text-base">Field</h2>
                <p className="text-sm text-brand-muted mt-1">
                  {CHECK_FIELD_TYPE_LABELS[selectedField.fieldType] ||
                    selectedField.fieldType}
                  {selectedBand ? ` · ${selectedBand.label}` : ""}
                </p>
              </div>

              {selectedField.fieldType === "prizeLines" ? (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-brand-charcoal">
                    Prize line columns
                  </legend>
                  <p className="text-xs text-brand-muted">
                    One row per prize. Amount is always included. Place payee,
                    tournament name, and date as separate fields.
                  </p>
                  {PRIZE_LINE_COLUMN_OPTIONS.map((column) => (
                    <label
                      key={column.key}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={
                          column.required ||
                          Boolean(
                            selectedField.prizeLineColumns?.[column.key]
                          ) ||
                          (selectedField.prizeLineColumns == null &&
                            (column.key === "eventName" ||
                              column.key === "place" ||
                              column.key === "amount"))
                        }
                        disabled={!canManageTournaments || column.required}
                        onChange={(event) =>
                          updateField(selectedId, {
                            prizeLineColumns: {
                              eventName: true,
                              place: true,
                              amount: true,
                              squadName: false,
                              bowlingCenterName: false,
                              teamName: false,
                              ...(selectedField.prizeLineColumns || {}),
                              [column.key]: event.target.checked,
                              amount: true,
                            },
                          })
                        }
                      />
                      {column.label}
                      {column.required ? " (always on)" : ""}
                    </label>
                  ))}
                </fieldset>
              ) : null}

              {selectedField.fieldType === "customText" ? (
                <label className="block text-sm">
                  <span className="block text-xs text-brand-muted mb-1">
                    Custom text
                  </span>
                  <input
                    className={inputClass}
                    value={selectedField.customText || ""}
                    maxLength={120}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        customText: event.target.value,
                      })
                    }
                  />
                </label>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="block text-xs text-brand-muted mb-1">
                    Font size (pt)
                  </span>
                  <input
                    type="number"
                    step="1"
                    className={inputClass}
                    value={selectedField.fontSizePt}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        fontSizePt: Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-xs text-brand-muted mb-1">
                    Alignment
                  </span>
                  <select
                    className={inputClass}
                    value={selectedField.textAlign}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        textAlign: event.target.value,
                      })
                    }
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label className="block text-sm col-span-2">
                  <span className="block text-xs text-brand-muted mb-1">
                    Long text
                  </span>
                  <select
                    className={inputClass}
                    value={selectedField.overflow}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        overflow: event.target.value,
                      })
                    }
                  >
                    <option value="truncate">Truncate</option>
                    <option value="wrap">Wrap</option>
                  </select>
                </label>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedField.fontBold)}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        fontBold: event.target.checked,
                      })
                    }
                  />
                  Bold
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedField.fontItalic)}
                    disabled={!canManageTournaments}
                    onChange={(event) =>
                      updateField(selectedId, {
                        fontItalic: event.target.checked,
                      })
                    }
                  />
                  Italic
                </label>
              </div>

              {canManageTournaments &&
              template.pageLayout === "voucher" &&
              selectedBand &&
              selectedBand.id !== "check" ? (
                <SecondaryButton onClick={copyToOtherStub}>
                  Copy to other stub
                </SecondaryButton>
              ) : null}

              <details className="border border-brand-charcoal/15 rounded-lg px-3 py-2">
                <summary className="text-sm cursor-pointer select-none">
                  Position and size
                </summary>
                <p className="text-xs text-brand-muted mt-2 mb-3">
                  Drag the box on the page, or enter inches here.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["xInches", "X (in)"],
                    ["yInches", "Y (in)"],
                    ["widthInches", "Width (in)"],
                    ["heightInches", "Height (in)"],
                  ].map(([key, label]) => (
                    <label key={key} className="block text-sm">
                      <span className="block text-xs text-brand-muted mb-1">
                        {label}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        className={inputClass}
                        value={selectedField[key]}
                        disabled={!canManageTournaments}
                        onChange={(event) =>
                          updateField(selectedId, {
                            [key]: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </details>

              {canManageTournaments ? (
                <SecondaryButton onClick={removeSelected} icon={FaTrash}>
                  Delete field
                </SecondaryButton>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-brand-muted">
              Select a field on the page to edit it. Arrow keys nudge. Delete
              removes it.
            </p>
          )}
        </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          applyDefaultLayout();
          setConfirmReset(false);
        }}
        title="Use the default layout?"
        message={
          template.pageLayout === "threePerPage"
            ? "This replaces the fields on the page with the standard check-face layout. Current field positions will be lost."
            : "This replaces the fields on the page with the standard 3-part layout: check on top, prize lines on both stubs. Current field positions will be lost."
        }
        confirmText="Use default layout"
        variant="warning"
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete this check template?"
        message="This template will be removed for the organization. Existing printed PDFs are not affected."
        confirmText="Delete template"
      />
    </div>
  );
};

export default CheckTemplateEditor;
